import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Json } from '@/integrations/supabase/types';
import { upsertDisplayYear } from '@/utils/displayYearUpsert';

export interface DisplaySong {
  id?: string;
  title: string;
  artist: string;
  duration?: string;
  year_introduced: number;
  youtube_url?: string;
  display_year_id?: string;
}

export interface DisplaySchedule {
  start_date: string;
  end_date: string;
  days: string[];
  hours: {
    start: string;
    end: string;
  };
  [key: string]: unknown;
}

export interface DisplayForm {
  name: string;
  description: string;
  location: string;
  display_type: string;
  holidays: string[];
  light_count: number | null;
  projector_used: boolean;
  illumibot_used: boolean;
  schedule: DisplaySchedule | null;
  images: string[];
  songs: DisplaySong[];
}

const INITIAL_SONG: DisplaySong = {
  title: '',
  artist: '',
  year_introduced: new Date().getFullYear(),
  youtube_url: ''
};

const INITIAL_FORM: DisplayForm = {
  name: '',
  description: '',
  location: '',
  display_type: 'Residential',
  holidays: ['Christmas'],
  light_count: null,
  projector_used: false,
  illumibot_used: false,
  schedule: null,
  images: [],
  songs: [{ ...INITIAL_SONG }]
};

export const useDisplayForm = (displayId?: string, onSuccess?: (displayId: string, displayData: any) => void) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [form, setForm] = useState<DisplayForm>(INITIAL_FORM);
  const [isLoading, setIsLoading] = useState(false);
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);

  const loadDisplay = useCallback(async (id: string) => {
    try {
      setIsLoading(true);
      
      const { data: display, error } = await supabase
        .from('displays')
        .select(`
          *,
          display_years (
            id,
            year,
            description,
            display_songs (
              id,
              title,
              artist,
              duration,
              year_introduced,
              sequence_file_url
            )
          )
        `)
        .eq('id', id)
        .single();

      if (error) {
        throw error;
      }

      if (!display) {
        throw new Error('Display not found');
      }

      // Check if user owns this display
      if (display.user_id !== user?.id) {
        toast({
          title: "Access Denied",
          description: "You don't have permission to edit this display.",
          variant: "destructive",
        });
        navigate('/profile');
        return;
      }

      // Convert the data to form format
      const currentYear = new Date().getFullYear();
      const currentYearData = display.display_years?.find(
        (year: { year: number; id: string; display_songs?: unknown[] }) => year.year === currentYear
      );

      const songs = currentYearData?.display_songs?.map((song: {
        id: string;
        title: string;
        artist: string;
        duration: string | null;
        year_introduced: number;
        sequence_file_url: string | null;
      }) => ({
        id: song.id,
        title: song.title,
        artist: song.artist,
        duration: song.duration,
        year_introduced: song.year_introduced,
        youtube_url: song.sequence_file_url,
        display_year_id: currentYearData.id
      })) || [{ ...INITIAL_SONG }];

      setForm({
        name: display.name,
        description: display.description || '',
        location: display.location,
        display_type: display.display_type,
        holidays: display.holiday_type ? display.holiday_type.split(',') : ['Christmas'],
        light_count: display.light_count || null,
        projector_used: display.projector_used || false,
        illumibot_used: display.illumibot_used || false,
        schedule: display.schedule as DisplaySchedule | null,
        // Prefer photos array if present, otherwise fall back to single image_url
        images: (Array.isArray((display as any).photos) && (display as any).photos.length > 0)
          ? ((display as any).photos as string[]).slice(0, 10)
          : (display.image_url ? [display.image_url] : []),
        songs
      });

      setLatitude(display.latitude);
      setLongitude(display.longitude);

    } catch (error) {
      console.error('Error loading display:', error);
      toast({
        title: "Error",
        description: "Failed to load display data.",
        variant: "destructive",
      });
      navigate('/profile');
    } finally {
      setIsLoading(false);
    }
  }, [navigate, toast, user?.id]);

  useEffect(() => {
    if (displayId) {
      loadDisplay(displayId);
    }
  }, [displayId, loadDisplay]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please log in to save your display.",
        variant: "destructive",
      });
      return;
    }

    if (!form.name.trim()) {
      toast({
        title: "Validation Error",
        description: "Display name is required.",
        variant: "destructive",
      });
      return;
    }

    if (!form.location.trim()) {
      toast({
        title: "Validation Error", 
        description: "Location is required.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsLoading(true);

      const displayData = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        location: form.location.trim(),
        display_type: form.display_type,
        holiday_type: form.holidays.join(','),
        light_count: form.light_count,
        projector_used: form.projector_used,
        illumibot_used: form.illumibot_used,
        schedule: form.schedule as Json,
        image_url: form.images[0] || null,
        // Persist up to 10 photos
        photos: (form.images || []).slice(0, 10),
        latitude,
        longitude,
        user_id: user.id
      };

      let finalDisplayId: string;

      if (displayId) {
        // Update existing display
        const { error: updateError } = await supabase
          .from('displays')
          .update(displayData)
          .eq('id', displayId);

        if (updateError) {
          throw new Error(updateError.message || 'Failed to update display');
        }
        finalDisplayId = displayId;
      } else {
        // Create new display
        const { data: newDisplay, error: insertError } = await supabase
          .from('displays')
          .insert(displayData)
          .select()
          .single();

        if (insertError || !newDisplay) {
          throw new Error(insertError?.message || 'Failed to create display');
        }
        finalDisplayId = newDisplay.id;
      }

      // Handle songs
      if (finalDisplayId) {
        // Get or create display year using the utility function
        const displayYear = await upsertDisplayYear(parseInt(finalDisplayId, 10));

        if (!displayYear) {
          throw new Error('Failed to create display year');
        }

        // Delete existing songs for this display year
        const { error: deleteError } = await supabase
          .from('display_songs')
          .delete()
          .eq('display_year_id', displayYear.id);

        if (deleteError) {
          throw new Error('Failed to update songs');
        }

        // Insert new songs
        const validSongs = form.songs.filter(song => 
          song.title?.trim() && 
          song.artist?.trim() && 
          song.year_introduced
        ).map(song => ({
          title: song.title.trim(),
          artist: song.artist.trim(),
          duration: song.duration?.trim() || null,
          year_introduced: song.year_introduced,
          sequence_file_url: song.youtube_url?.trim() || null,
          display_year_id: displayYear.id
        }));

        if (validSongs.length > 0) {
          const { error: songsError } = await supabase
            .from('display_songs')
            .insert(validSongs);

          if (songsError) {
            throw new Error('Failed to save songs');
          }
        }
      }

      if (onSuccess) {
        // Call the success callback with display data for the modal
        onSuccess(finalDisplayId, {
          title: form.name,
          id: finalDisplayId
        });
      } else {
        toast({
          title: "Success",
          description: "Display saved successfully!",
        });
        navigate('/profile');
      }
    } catch (error) {
      console.error('Error saving display:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to save display.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    form,
    setForm,
    isLoading,
    latitude,
    longitude,
    setLatitude,
    setLongitude,
    handleSubmit
  };
};